/**
 * IndustryPackSelector.jsx
 * =========================
 * Industry Pack selection and application UI.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { 
  Package, Search, ChevronRight, Building2, 
  FileText, Sparkles, Check, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IndustryPackSelector = ({ documentId, onPackApplied }) => {
  const [packs, setPacks] = useState([]);
  const [categories, setCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [applying, setApplying] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/industry-packs/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPacks(data.packs);
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch packs:', error);
      toast.error('Failed to load industry packs');
    } finally {
      setLoading(false);
    }
  };

  const applyPack = async (packId) => {
    if (!documentId) {
      toast.error('No document selected');
      return;
    }

    setApplying(packId);

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(
        `${API_URL}/api/agentic-extensions/industry-packs/${packId}/apply?doc_id=${documentId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to apply pack');
      
      const data = await response.json();
      toast.success(`${data.pack_applied?.pack_name} applied successfully!`);
      onPackApplied?.(data);
    } catch (error) {
      console.error('Apply pack error:', error);
      toast.error('Failed to apply industry pack');
    } finally {
      setApplying(null);
    }
  };

  const filteredPacks = packs.filter(pack => {
    const matchesSearch = !searchQuery || 
      pack.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pack.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || pack.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedPacks = filteredPacks.reduce((acc, pack) => {
    const cat = pack.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(pack);
    return acc;
  }, {});

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="industry-pack-selector">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <CardTitle>Industry Packs</CardTitle>
        </div>
        <CardDescription>
          Apply industry-specific document processing templates
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Search & Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search industry packs..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(null)}
          >
            All ({packs.length})
          </Button>
          {Object.entries(categories).map(([key, name]) => {
            const count = packs.filter(p => p.category === key).length;
            return (
              <Button
                key={key}
                variant={selectedCategory === key ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(key)}
              >
                {name} ({count})
              </Button>
            );
          })}
        </div>

        {/* Packs List */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(groupedPacks).map(([category, categoryPacks]) => (
            <div key={category}>
              <h4 className="font-medium text-sm text-muted-foreground mb-2">
                {categories[category] || category}
              </h4>
              <div className="space-y-2">
                {categoryPacks.map(pack => (
                  <div
                    key={pack.id}
                    className="p-3 border rounded-lg hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-primary" />
                          {pack.name}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {pack.description}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            <FileText className="h-3 w-3 mr-1" />
                            {pack.doc_types_count} types
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {pack.entities_count} entities
                          </Badge>
                          {pack.has_ai_features && (
                            <Badge variant="secondary" className="text-xs">
                              <Sparkles className="h-3 w-3 mr-1" />
                              AI
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {documentId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => applyPack(pack.id)}
                          disabled={applying === pack.id}
                        >
                          {applying === pack.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              Apply
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredPacks.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No packs found matching your criteria</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IndustryPackSelector;
